import React from 'react';
import type { MemberTabId } from './MemberTabs';

interface MemberActionsHubProps {
  hasTimeline: boolean;
  timelineLoading: boolean;
  onUpload: () => void;
  onGenerateTimeline: () => void;
  onSwitchTab: (tab: MemberTabId) => void;
}

const IconUpload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const IconChat = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const MemberActionsHub: React.FC<MemberActionsHubProps> = ({
  hasTimeline,
  timelineLoading,
  onUpload,
  onGenerateTimeline,
  onSwitchTab,
}) => {
  // The chat bubble lives globally as a FAB; we open it programmatically
  // by clicking the FAB DOM node so we don't have to thread props/contexts
  // through the entire app for this single hand-off.
  const askAi = () => {
    const fab = document.querySelector<HTMLButtonElement>('.medical-chat-widget-fab');
    if (fab) {
      // Only "click to open" when the FAB is currently in its closed state;
      // otherwise we'd toggle the panel back closed for already-open chats.
      const isOpen = fab.classList.contains('medical-chat-widget-fab-open');
      if (!isOpen) fab.click();
      fab.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  return (
    <div className="ov-actions">
      <button type="button" className="ov-action" onClick={onUpload}>
        <span className="ov-action-icon"><IconUpload /></span>
        <span className="ov-action-text">
          <span className="ov-action-title">Încarcă document</span>
          <span className="ov-action-sub">PDF sau imagini · clasificare AI automată</span>
        </span>
      </button>

      <button
        type="button"
        className="ov-action"
        disabled={timelineLoading}
        onClick={() => {
          onSwitchTab('timeline');
          onGenerateTimeline();
        }}
      >
        <span className="ov-action-icon"><IconRefresh /></span>
        <span className="ov-action-text">
          <span className="ov-action-title">
            {hasTimeline ? 'Regenerează cronologia' : 'Generează cronologia'}
          </span>
          <span className="ov-action-sub">
            {hasTimeline
              ? 'Reanalizează toate documentele și actualizează evenimentele'
              : 'Construiește o cronologie medicală structurată din documente'}
          </span>
        </span>
      </button>

      <button type="button" className="ov-action" onClick={askAi}>
        <span className="ov-action-icon"><IconChat /></span>
        <span className="ov-action-text">
          <span className="ov-action-title">Întreabă AI-ul</span>
          <span className="ov-action-sub">Pune întrebări despre dosarul medical al membrului</span>
        </span>
      </button>
    </div>
  );
};

export default MemberActionsHub;
